import { Translation, useSafetyTranslation } from 'cng-common/features/translations'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import type { IPartnerDocumentSection } from '../../../../lib/documentsAndLinks'
import { CockpitBreadcrumb } from '../../../components/cockpitBreadcrumb'
import { CockpitGrouping, type ICockpitGroupingOption } from '../../../components/cockpitGrouping'
import type {
  IDocumentGroup,
  IDocumentGroupCrumb,
  TDocumentsGrouping,
  TDocumentType,
} from '../../types'

import styles from './DocumentsGroupedControls.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.documentsAndLinks.grouping

const GROUPING_OPTIONS: ICockpitGroupingOption<TDocumentsGrouping>[] = [
  { value: 'geo', label: <Translation span text={KEYS.geo} /> },
  { value: 'creator', label: <Translation span text={KEYS.creator} /> },
  { value: 'partner', label: <Translation span text={KEYS.partner} /> },
]

interface IProps {
  showGrouping: boolean
  isSinglePartner: boolean
  documentType: TDocumentType | null
  grouping: TDocumentsGrouping
  geoPath: IDocumentGroupCrumb[]
  selected?: IDocumentGroup
  selectedPartner?: IPartnerDocumentSection
  openType: (type: TDocumentType | null) => void
  resetGroups: () => void
  goToGeoLevel: (index: number) => void
  selectPartner: (partnerId: string | null) => void
  changeGrouping: (grouping: TDocumentsGrouping) => void
}

export const DocumentsGroupedControls = ({
  showGrouping,
  isSinglePartner,
  documentType,
  grouping,
  geoPath,
  selected,
  selectedPartner,
  openType,
  resetGroups,
  goToGeoLevel,
  selectPartner,
  changeGrouping,
}: IProps) => {
  const t = useSafetyTranslation()
  if (!documentType) {
    return null
  }
  const steps = [
    { id: 'document-type', label: `${documentType.toUpperCase()} documents`, onClick: resetGroups },
    ...(isSinglePartner ? [] : geoPath).map((step, index) => ({
      ...step,
      onClick: () => goToGeoLevel(index),
    })),
    ...(!isSinglePartner && selected
      ? [{ id: `group:${selected.id}`, label: selected.label, onClick: () => selectPartner(null) }]
      : []),
  ]
  const crumbs = !isSinglePartner && selectedPartner ? steps : steps.slice(0, -1)
  const current =
    (!isSinglePartner && selectedPartner?.partnerName) || steps[steps.length - 1].label
  return (
    <div className={styles.controls}>
      <CockpitBreadcrumb
        rootLabel="All documents"
        ariaLabel="Document groups"
        crumbs={crumbs}
        current={current}
        onNavigate={(index) => {
          if (index < 0) {
            openType(null)
            return
          }
          steps[index].onClick()
        }}
      />
      {showGrouping && (
        <CockpitGrouping
          value={grouping}
          options={GROUPING_OPTIONS}
          label={<Translation span text={KEYS.label} />}
          ariaLabel={t(KEYS.ariaLabel).replaceAll('{grouping}', t(KEYS[grouping]))}
          optionsAriaLabel={t(KEYS.label)}
          onChange={changeGrouping}
        />
      )}
    </div>
  )
}
